import {createApi} from '@reduxjs/toolkit/query/react'
import {fetchBaseFPNetQuery} from "@commons/utils/fetchBaseFPNetQuery";
import {Lavoratori} from "@commons/mapping/DataTablesSchema";
import {addBaseQueryParams, addURI, BaseQueryParams, transformResponse} from "@commons/utils/APIParams";
import {LavoratoreRecord} from "@commons/mapping/LavoratoreRecord";
import {config} from "@commons/config.instance";
import {CDate, PeriodoType} from "@commons/types";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";

//https://fpnet2.ericed.it/FPNet2021/lavoratore/1-1/flat?draw=2&start=0&length=100&search%5Bvalue%5D=&search%5Bregex%5D=false&iniziale=*&tutti=&inForza=p&forceDataDa=20221101&forceDataA=20221130&auth_token=c201059bce020ea707d831bb37d07d03&nowId=1669901587011&mode=DataTables&nowId=1669901587011&view=Gri

export interface LavoratoreFilterSelection extends BaseQueryParams {
    idAziendaMaster: number | null,
    idAzienda: number | null,
    tipo: 'flat' | 'tdet' | 'trasformabile' | 'prorogabile',

    iniziale: string, // '*' per tutti
    qualifiche: string[], // [] per tutti

    tutti: 'all' | null,

    inForzaSection: boolean,
    inForza: PeriodoType,
    forceDataDa: CDate,
    forceDataA: CDate,

    hiredSection: boolean,
    hired: PeriodoType,
    hiredDataDa: CDate,
    hiredDataA: CDate,

    firedSection: boolean,
    fired: PeriodoType,
    firedDataDa: CDate,
    firedDataA: CDate,
}

function isCustom(flag: PeriodoType) {
    return (flag === 'p' || flag === 'custom')
}

export const lavoratoriApi = createApi({
    reducerPath: 'lavoratori.lavoratoriApi',
    baseQuery: fetchBaseFPNetQuery({baseUrl: config.getBaseUrl('FPNet')}),
    tagTypes: ['Lavoratori'],
    endpoints: (builder) => ({

        getLavoratori: builder.query<Lavoratori, Partial<LavoratoreFilterSelection>>({
            query: (filter) => {
                let params: string[] = [];
                if (filter.iniziale) params.push('iniziale=' + filter.iniziale);
                if (filter.tutti) params.push('tutti=' + filter.tutti);
                else {
                    if (filter.inForzaSection && filter.inForza) {
                        params.push('inForza=' + filter.inForza);
                        if (isCustom(filter.inForza) && filter.forceDataDa) params.push('forceDataDa=' + filter.forceDataDa);
                        if (isCustom(filter.inForza) && filter.forceDataA) params.push('forceDataA=' + filter.forceDataA);
                    }
                    if (filter.hiredSection && filter.hired) {
                        params.push('hired=' + filter.hired);
                        if (isCustom(filter.hired) && filter.hiredDataDa) params.push('hiredDataDa=' + filter.hiredDataDa);
                        if (isCustom(filter.hired) && filter.hiredDataA) params.push('hiredDataA=' + filter.hiredDataA);
                    }
                    if (filter.firedSection && filter.fired) {
                        params.push('fired=' + filter.fired);
                        if (isCustom(filter.fired) && filter.firedDataDa) params.push('firedDataDa=' + filter.firedDataDa);
                        if (isCustom(filter.fired) && filter.firedDataA) params.push('firedDataA=' + filter.firedDataA);
                    }
                }
                if (filter.qualifiche && filter.qualifiche.length > 0) {
                    for (let qualifica of filter.qualifiche)
                        params.push('qualifiche[]=' + qualifica);
                }

                addBaseQueryParams(filter, params);

                return `/lavoratore/${filter.idAziendaMaster}-${filter.idAzienda}/${filter.tipo}?${params.length > 0 ? '&' + encodeURI(params.join("&")) : ''}`;
            },
            transformResponse
        }),

        getLavoratore: builder.query<LavoratoreRecord, { idDip: number }>({
            query: ({idDip}) => {

                return `/lavoratore/${idDip}?view=Full`;
            },
            transformResponse: addURI
        }),

        getLavoratoreXTerminaleVirtuale: builder.query<AziendaRecord, { idAziendaMaster: number, idAzienda: number }>({
            query: ({idAziendaMaster, idAzienda}) => {

                return `/lavoratore/${idAziendaMaster}-${idAzienda}/FLAT`;
            },
            transformResponse: addURI
        }),

        updateLavoratore: builder.mutation<Partial<LavoratoreRecord>, Partial<LavoratoreRecord> & Pick<LavoratoreRecord, 'URI'>>({
            // note: an optional `queryFn` may be used in place of `query`
            query: ({URI, ...patch}) => ({
                url: URI ? URI : '',
                method: 'PUT', // 'PATCH'
                body: patch,
            }),
            transformResponse,
            invalidatesTags: (result, error, {URI}) => [{type: 'Lavoratori', URI}],
        }),

    }),
})
export const {
    useGetLavoratoriQuery,
    useLazyGetLavoratoriQuery,
    useGetLavoratoreQuery,
    useGetLavoratoreXTerminaleVirtualeQuery,
    useLazyGetLavoratoreQuery,
    useUpdateLavoratoreMutation
} = lavoratoriApi;